import { useCallback, useEffect, useMemo, useRef, useState, forwardRef } from 'react';
import Slide from '@mui/material/Slide';
import {
  Dialog, DialogContent, DialogActions, Box, Stack, Typography,
  Button, IconButton, Chip, CircularProgress, Alert, Tooltip,
  Snackbar, Tabs, Tab,
} from '@mui/material';
import {
  Close as CloseIcon, Print as PrintIcon,
  GridOn as ExcelIcon, AutoStories as SheetIcon,
  CheckCircle as SavedIcon, ErrorOutline as ErrorIcon,
  Sync as PendingIcon,
} from '@mui/icons-material';
// Import the pre-bundled browser build directly. `from 'exceljs'`
// alone resolves to the Node entry which references `process`, and
// CRA 4 doesn't polyfill that — the whole bundle then crashes on
// load with "process is not defined" and blanks the page.
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import API from '../../BaseURL';
import swal from '../../utils/swal';
// ?view_as=<id> — an admin previewing a teacher's portal. Read from the
// URL, so on the admin's own Register Subject page (no such param) this
// sheet behaves exactly as it always has.
import useViewAs from '../../teacher/useViewAs';

// RawScoreSheetDialog — the printable AND interactive entry form for
// teachers. Same layout as before plus three things:
//
//   1. Every mark cell is now an <input>. Teachers can type marks
//      directly into the on-screen sheet.
//   2. The Total cell at the end of each row is computed live from the
//      formula the active assessment profile implies.
//        SBA: total = Σ (avg(items in component) / 100 × weight_pct)
//        Simple+Term: total = (cs/cl_res × cl_sc) + (es/ex_res × exa_sc)
//        Simple+Mock/Mid: total = the raw score
//   3. Each row autosaves on its own (600 ms after the last keystroke),
//      so there's no Save button — if the page crashes mid-entry,
//      whatever was typed and stopped being touched is already in the
//      database. A small icon next to each row's name says
//      pending / saved / error.
//
// Print still works (uses whatever values are currently in the inputs)
// and Excel export now embeds the formulas so teachers can also fill
// the .xls offline and we can re-import it later.

const PRINT_CSS = `
  @page { size: A4 landscape; margin: 10mm; }
  body { margin: 0; font-family: 'Times New Roman', Georgia, serif; color: #111; }
  .rss-sheet { page-break-after: always; padding: 2mm; }
  .rss-sheet:last-of-type { page-break-after: auto; }
  .rss-title { text-align: center; font-weight: 800; font-size: 13pt; margin: 0 0 1.5mm; text-transform: uppercase; letter-spacing: 0.5px; }
  .rss-meta  { text-align: center; font-size: 9.5pt; margin-bottom: 2mm; color: #333; }
  .rss-meta b { color: #000; }
  table.rss { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 8.8pt; table-layout: auto; }
  table.rss th, table.rss td {
    box-sizing: border-box;
    border-right: 1px solid #5c6bc0;
    border-bottom: 1px solid #5c6bc0;
    padding: 2px 4px;
  }
  table.rss thead th { border-top: 1px solid #1a237e; }
  table.rss thead th:first-child,
  table.rss tbody td:first-child { border-left: 1px solid #5c6bc0; }
  table.rss thead th {
    background: #3949ab; color: #fff;
    text-align: center; font-weight: 700; font-size: 8pt;
    text-transform: uppercase; letter-spacing: 0.3px;
    padding: 4px 3px; white-space: nowrap;
  }
  table.rss td.cell-num     { width: 26px; text-align: right; color: #777; background: #fff; }
  table.rss td.cell-ind     { width: 64px; font-family: Consolas, Menlo, monospace; font-size: 8.5pt; background: #fff; }
  /* Name capped tight — long names ellipsis-truncate and the
     full name still shows up in the dialog row title on hover. */
  table.rss td.cell-name    { font-weight: 600; white-space: nowrap; max-width: 110px; overflow: hidden; text-overflow: ellipsis; background: #fff; }
  /* Mark cells hold up to 3 digits (0–100). Slightly wider so the
     abbreviated subject header reads at the same weight as the
     Total / Pos / Grade / Remarks headers. */
  table.rss td.cell-mark    { height: 24px; text-align: right; padding: 0; width: 66px; min-width: 60px; max-width: 76px; background: #fff; }
  table.rss thead th.head-mark { width: 66px; min-width: 60px; max-width: 76px; font-size: 9pt; padding: 6px 4px; letter-spacing: 0.2px; }
  table.rss td.cell-mark input { font-size: 10pt; padding: 3px 5px; }

  /* The four sticky result columns. Widths INCLUDE padding+borders
     (box-sizing: border-box above) so the sticky right offsets match
     the rendered widths exactly — no header text bleeds through. */
  table.rss td.cell-total,   table.rss thead th.head-total   { width: 70px;  min-width: 70px;  font-weight: 800; background: #e3f2fd; color: #0d47a1; text-align: right; }
  table.rss td.cell-pos,     table.rss thead th.head-pos     { width: 60px;  min-width: 60px;  font-weight: 800; background: #e8f5e9; color: #1b5e20; text-align: center; }
  table.rss td.cell-grade,   table.rss thead th.head-grade   { width: 64px;  min-width: 64px;  font-weight: 800; background: #f3e5f5; color: #4a148c; text-align: center; letter-spacing: 0.3px; }
  table.rss td.cell-remarks, table.rss thead th.head-remarks { width: 110px; min-width: 110px; font-weight: 600; background: #fce4ec; color: #880e4f; text-align: center; font-style: italic; font-size: 8.3pt; }
  table.rss thead th.head-total   { background: #1565c0; color: #fff; }
  table.rss thead th.head-pos     { background: #2e7d32; color: #fff; }
  table.rss thead th.head-grade   { background: #6a1b9a; color: #fff; }
  table.rss thead th.head-remarks { background: #ad1457; color: #fff; }

  /* Pin the four result columns to the right edge of the scroll
     container so Total / Pos / Grade / Remarks are always visible.
     Right offsets MUST match the rendered widths above exactly. */
  .rss-scroll table.rss td.cell-remarks,
  .rss-scroll table.rss thead th.head-remarks { position: sticky; right: 0;   z-index: 3; }
  .rss-scroll table.rss td.cell-grade,
  .rss-scroll table.rss thead th.head-grade   { position: sticky; right: 110px; z-index: 3; }
  .rss-scroll table.rss td.cell-pos,
  .rss-scroll table.rss thead th.head-pos     { position: sticky; right: 174px; z-index: 3; }
  .rss-scroll table.rss td.cell-total,
  .rss-scroll table.rss thead th.head-total   { position: sticky; right: 234px; z-index: 3;
                                                box-shadow: -4px 0 6px -3px rgba(0,0,0,0.22); }
  table.rss td.cell-mark input,
  table.rss td.cell-total input {
    width: 100%; border: 0; background: transparent;
    font: inherit; text-align: right; padding: 2px 4px; outline: none;
    box-sizing: border-box;
  }
  table.rss td.cell-mark input:focus { background: #fff59d; }
  .rss-formula { text-align: center; font-size: 8.5pt; color: #555; margin-top: 1mm; }
  .rss-footer { display: flex; justify-content: space-between; margin-top: 5mm; font-size: 9pt; }
  .rss-footer .sig { width: 60mm; border-top: 1px dotted #000; padding-top: 2px; text-align: center; }
  .rss-scroll { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
  @media print {
    table.rss td.cell-mark input,
    table.rss td.cell-total input { background: transparent; outline: 0; }
    .no-print { display: none !important; }
    .rss-scroll { overflow: visible !important; }
  }
`;

// ────────────────────────────────────────────────────────────────
// Column + formula builders
// ────────────────────────────────────────────────────────────────

// Returns the column descriptors for one subject:
//   [{ key, header, component, item_index, weight_pct, is_exam, max }]
// The Total column is rendered separately (computed, not a real entry).
function buildColumns(profile, state) {
  // A Mock or Mid-Term Test is ONE paper and ONE mark. No SBA, no class
  // score weighed against an exam score — the learner sat the paper and
  // got 78, and 78 is the whole of it. The server says so by sending
  // mode 'single' (see utils/examTypeSettings.js), and the header
  // carries what the paper is marked out of so the teacher can see the
  // denominator above the box they are typing in.
  if (profile?.mode === 'single') {
    const max = Math.max(1, Number(profile.max_score) || 100);
    return [{ key: 'score', header: `Score / ${max}`, kind: 'simple', max }];
  }
  if (!profile || profile.mode === 'sba') {
    const comps = profile?.components || [];
    if (comps.length === 0) {
      return [{ key: 'score', header: 'Score', kind: 'simple', max: 100 }];
    }
    const cols = [];
    for (const c of comps) {
      const n = Math.max(1, Number(c.item_count) || 1);
      const weight = Number(c.weight_pct) || 0;
      const perMax = Math.max(0.01, Number(c.max_mark_per_item) || 100);
      for (let i = 1; i <= n; i++) {
        const base = n === 1 ? c.label : `${c.label} ${i}`;
        cols.push({
          key: `${c.label}-${i}`,
          // Show "/ N" denominator in the header so the teacher sees
          // the per-item max right above the cell they're typing into.
          header: `${base} / ${perMax}`,
          kind: 'sba',
          component: c.label,
          item_index: i,
          item_count: n,
          weight_pct: weight,
          is_exam: !!c.is_exam,
          max: perMax,
        });
      }
    }
    return cols;
  }
  if (state === 'Term Exam' || state === 'Terminal') {
    const clRes = Number(profile.cl_res) || 60;
    const exRes = Number(profile.ex_res) || 100;
    const clSc  = Number(profile.cl_sc)  || 50;
    const exaSc = Number(profile.exa_sc) || 50;
    return [
      { key: 'class', header: `Class Score / ${clRes}`, kind: 'simple-class', max: clRes, weight_pct: clSc, denom: clRes },
      { key: 'exam',  header: `Exam Score / ${exRes}`,  kind: 'simple-exam',  max: exRes, weight_pct: exaSc, denom: exRes },
    ];
  }
  return [{ key: 'score', header: 'Score', kind: 'simple', max: 100 }];
}

// Compute the final total for one row given the column descriptors
// and the item values keyed by column.key.
function computeTotal(columns, items, profile, state) {
  const num = (k) => {
    const v = items[k];
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  // SBA — group by component, average each, normalise by the
  // component's per-item max, then multiply by weight%.
  const isSba = profile?.mode === 'sba' && columns.some(c => c.kind === 'sba');
  if (isSba) {
    const byComp = new Map();
    for (const c of columns) {
      if (c.kind !== 'sba') continue;
      if (!byComp.has(c.component)) {
        byComp.set(c.component, {
          weight: c.weight_pct,
          perMax: Math.max(0.01, Number(c.max) || 100),
          items: [],
        });
      }
      const v = num(c.key);
      if (v !== null) byComp.get(c.component).items.push(v);
    }
    // Return null when no marks have been entered yet, so the Grade /
    // Remarks lookup doesn't misclassify an empty row as a F9 / Fail.
    const anyEntered = [...byComp.values()].some(g => g.items.length > 0);
    if (!anyEntered) return null;
    let total = 0;
    for (const { weight, perMax, items: vals } of byComp.values()) {
      if (vals.length === 0) continue;
      const avg = vals.reduce((s, x) => s + x, 0) / vals.length;
      total += (avg / perMax) * weight;
    }
    return round2(total);
  }
  // Mock / Mid-Term — the mark on the paper, scaled if the school has
  // said the paper is out of something other than what it reports out
  // of. Unscaled (the default) this is just the number typed.
  if (profile?.mode === 'single') {
    const raw = num('score');
    if (raw === null) return null;
    const max   = Math.max(1, Number(profile.max_score) || 100);
    const scale = Math.max(1, Number(profile.scale_to)  || 100);
    return round2(max === scale ? raw : (raw / max) * scale);
  }
  // Simple + terminal
  if (state === 'Term Exam' || state === 'Terminal') {
    const cls = num('class');
    const exm = num('exam');
    const cCol = columns.find(c => c.kind === 'simple-class');
    const eCol = columns.find(c => c.kind === 'simple-exam');
    if (cls === null && exm === null) return null;
    let total = 0;
    if (cls !== null && cCol) total += (cls / (cCol.denom || 1)) * (cCol.weight_pct || 0);
    if (exm !== null && eCol) total += (exm / (eCol.denom || 1)) * (eCol.weight_pct || 0);
    return round2(total);
  }
  // Simple + mock/mid — total IS the raw score
  return num('score');
}

function round2(n) { return Math.round(Number(n || 0) * 100) / 100; }

// Default grading bands — used only when the school hasn't set up its
// own. Mirrors the canonical WAEC / Ghana SBA scale.
const DEFAULT_GRADES = [
  { marks_1: 80, marks_2: 100, grade: 'A1', remarks: 'Excellent' },
  { marks_1: 70, marks_2: 79,  grade: 'B2', remarks: 'Very Good' },
  { marks_1: 60, marks_2: 69,  grade: 'B3', remarks: 'Good' },
  { marks_1: 55, marks_2: 59,  grade: 'C4', remarks: 'Credit' },
  { marks_1: 50, marks_2: 54,  grade: 'C5', remarks: 'Credit' },
  { marks_1: 45, marks_2: 49,  grade: 'C6', remarks: 'Credit' },
  { marks_1: 40, marks_2: 44,  grade: 'D7', remarks: 'Pass' },
  { marks_1: 35, marks_2: 39,  grade: 'E8', remarks: 'Weak Pass' },
  { marks_1: 0,  marks_2: 34,  grade: 'F9', remarks: 'Fail' },
];

// Return the band a Total falls into, using the school's grading table
// (data.grades) if present, falling back to DEFAULT_GRADES otherwise.
//
// Defensive about how the school stored the two columns: takes
// MIN(marks_1, marks_2) as the lower bound and MAX as the upper,
// then tests bands from highest down. If nothing matches, returns
// null — better empty than misclassified.
function gradeFor(total, grades) {
  if (total === null || total === undefined || total === '') return null;
  const t = Number(total);
  if (!Number.isFinite(t)) return null;
  const list = grades && grades.length > 0 ? grades : DEFAULT_GRADES;
  const bands = list.map(g => {
    const a = Number(g.marks_1);
    const b = Number(g.marks_2);
    const A = Number.isFinite(a) ? a : 0;
    const B = Number.isFinite(b) ? b : 0;
    return {
      lo: Math.min(A, B),
      hi: Math.max(A, B),
      grade:   String(g.grade   ?? ''),
      remarks: String(g.remarks ?? ''),
    };
  });
  // Highest upper-bound first, so a "first band whose lower bound
  // is <= t" pick correctly treats each band's upper edge as
  // exclusive of the next higher band's lower edge. This closes the
  // decimal gap: 79.87 lands in 70-79 (Very Good) instead of nothing.
  bands.sort((a, b) => b.hi - a.hi);
  for (const g of bands) {
    if (t >= g.lo) return { grade: g.grade, remarks: g.remarks };
  }
  return null;
}

// Squeeze the long generated column header into something that fits in
// a narrow mark cell. Full text stays available via the title tooltip.
//   "Assignment 1 / 10"      → "Asn 1 /10"
//   "Class Exercise 2 / 10"  → "CE 2 /10"
//   "End of Term Exam / 100" → "EoT Exam /100"
//   "Quiz / Test 1 / 100"    → "Quiz 1 /100"
//   "Project / 20"           → "Proj /20"
function compactHeader(header) {
  return String(header || '')
    .replace(/\bClass\s+Exercise\b/gi, 'CE')
    .replace(/\bExercise\b/gi, 'Ex')
    .replace(/\bAssignments?\b/gi, 'Asn')
    .replace(/\bEnd\s+of\s+Term\b/gi, 'EoT')
    .replace(/\bProject\b/gi, 'Proj')
    .replace(/\bQuiz\s*\/\s*Test\b/gi, 'Quiz')
    .replace(/\bMid[-\s]?Term\b/gi, 'Mid')
    .replace(/\bTerminal\b/gi, 'Term')
    .replace(/\s*\/\s*(\d)/g, ' /$1')   // tighten "1 / 10" → "1 /10"
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// English ordinal suffix — 1 → "1st", 22 → "22nd", 11 → "11th".
function ordinal(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num < 1) return '';
  const r100 = num % 100;
  if (r100 >= 11 && r100 <= 13) return `${num}th`;
  switch (num % 10) {
    case 1: return `${num}st`;
    case 2: return `${num}nd`;
    case 3: return `${num}rd`;
    default: return `${num}th`;
  }
}

// Standard competition ranking — ties share the same position, the
// next distinct total skips by the size of the tie (1, 2, 2, 4 …).
// Returns a map keyed by student index.
function computePositions(rows) {
  const valid = rows
    .filter(r => r.total !== null && r.total !== undefined && Number.isFinite(Number(r.total)))
    .map(r => ({ ind: r.ind, total: Number(r.total) }))
    .sort((a, b) => b.total - a.total);
  const out = {};
  for (let i = 0; i < valid.length; i++) {
    if (i > 0 && valid[i].total === valid[i - 1].total) {
      out[valid[i].ind] = out[valid[i - 1].ind];
    } else {
      out[valid[i].ind] = i + 1;
    }
  }
  return out;
}

// Human-readable explanation of the formula in play — printed under
// each subject's table so teachers know how the total was derived.
function formulaText(columns, profile, state) {
  if (!profile) return '';
  if (profile.mode === 'single') {
    const max   = Math.max(1, Number(profile.max_score) || 100);
    const scale = Math.max(1, Number(profile.scale_to)  || 100);
    return max === scale
      ? `One paper, one mark — the score out of ${max} is the total.`
      : `Total = Score / ${max} × ${scale}  (marked out of ${max}, reported out of ${scale})`;
  }
  if (profile.mode === 'sba') {
    const groups = new Map();
    for (const c of columns) {
      if (c.kind !== 'sba') continue;
      if (!groups.has(c.component)) {
        groups.set(c.component, { n: 0, w: c.weight_pct, m: Number(c.max) || 100 });
      }
      groups.get(c.component).n += 1;
    }
    const parts = [...groups.entries()].map(([label, { n, w, m }]) =>
      n === 1
        ? `(${label}/${m})×${w}%`
        : `(avg(${label} ${n} items)/${m})×${w}%`);
    return `Total = ${parts.join(' + ')}`;
  }
  if (state === 'Term Exam' || state === 'Terminal') {
    const c = columns.find(x => x.kind === 'simple-class');
    const e = columns.find(x => x.kind === 'simple-exam');
    return `Total = (Class / ${c?.denom || 60} × ${c?.weight_pct || 50}%) + (Exam / ${e?.denom || 100} × ${e?.weight_pct || 50}%)`;
  }
  return 'Total = entered Score';
}

// ────────────────────────────────────────────────────────────────
// HTML / Excel helpers
// ────────────────────────────────────────────────────────────────

// Slide-from-top transition for the "pull-down" mode used by the
// Teacher Portal. Module-scope so React keeps the same component
// reference across renders (otherwise the dialog would re-mount).
const SlideDown = forwardRef(function SlideDown(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c =>
    ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}

// Build the static print HTML — bakes in the CURRENT entry values so
// the printed sheet reflects what the teacher has on screen.
// Dedicated stylesheet for the print window. All banner rows live
// inside <thead> so they repeat automatically on every printed page
// when a class spills past one sheet — each page picks up the school
// name, the subject identifier and the column headings without us
// having to do anything.
const PRINT_REPORT_CSS = `
  @page { size: A4 landscape; margin: 8mm 10mm; }
  body { margin: 0; font-family: 'Calibri','Segoe UI',Arial,sans-serif; color: #111; }
  .rss-page { page-break-after: always; }
  .rss-page:last-of-type { page-break-after: auto; }

  table.rss-print { width: 100%; border-collapse: collapse; font-size: 10pt; }
  table.rss-print thead { display: table-header-group; }
  table.rss-print tbody { display: table-row-group; }

  table.rss-print thead .school-row td {
    background: #1a237e; color: #fff;
    font-size: 15pt; font-weight: 800;
    text-align: center;
    padding: 3mm 4mm 2mm;
    letter-spacing: 1px;
    border: 1px solid #1a237e;
    border-bottom: 2px solid #fff;
  }
  table.rss-print thead .period-row td {
    background: #e8eaf6; color: #1a237e;
    font-size: 12pt; font-weight: 700;
    text-align: center;
    padding: 2mm 4mm;
    border: 1px solid #9fa8da;
    letter-spacing: 0.4px;
  }
  table.rss-print thead .teacher-row td {
    background: #fafbfc;
    padding: 1.5mm 6mm;
    font-size: 9.5pt;
    border: 1px solid #cfd8dc;
    color: #37474f;
  }
  table.rss-print thead .teacher-row b { color: #455a64; font-weight: 700; }
  table.rss-print thead .head-row th {
    background: #3949ab; color: #fff;
    font-weight: 700; padding: 5px 6px;
    border: 1px solid #1a237e;
    text-transform: uppercase;
    font-size: 9pt; letter-spacing: 0.3px;
    text-align: center; vertical-align: middle;
  }

  table.rss-print tbody td {
    border: 1px solid #5c6bc0;
    padding: 4px 6px;
    vertical-align: middle;
  }
  table.rss-print tbody td.num   { text-align: center; color: #777; width: 28px; font-size: 9pt; }
  table.rss-print tbody td.ind   { font-family: Consolas, Menlo, monospace; font-size: 9.5pt;
    width: 80px; text-align: left; color: #37474f; }
  table.rss-print tbody td.name  { font-weight: 600; text-align: left; padding-left: 8px; }
  table.rss-print tbody td.mark  { text-align: center; height: 26px; font-size: 10pt; }
  table.rss-print tbody tr:nth-child(even) td { background: #fafbfc; }

  @media print {
    .no-print { display: none !important; }
  }
`;

// Build the print-ready HTML. Only the data-entry columns appear —
// Total, Position, Grade and Remarks are dropped because the printed
// sheet is meant for hand-filling, not for showing the auto-calculated
// results. The school banner, subject identifier and teacher row live
// in <thead> so they restamp at the top of every printed page when a
// class needs more than one sheet.
function htmlForPrint(data, columns, getRowValues, school = {}) {
  if (!data?.subjects?.length) {
    return `<div class="rss-page"><p style="text-align:center;color:#666;font-style:italic;padding:40mm 0">
      No subjects registered for ${escapeHtml(data?.class)} - ${escapeHtml(data?.term)} - ${escapeHtml(data?.state)}.
    </p></div>`;
  }
  const schoolName = escapeHtml(school.school_name || 'School');

  return data.subjects.map(s => {
    const totalCols = 3 + columns.length;
    const periodLine =
        `${escapeHtml(s.sub)} &nbsp;·&nbsp; ${escapeHtml(data.class)}`
      + ` &nbsp;·&nbsp; ${escapeHtml((data.term || '').replace(/_/g, ' '))}`
      + ` &nbsp;·&nbsp; ${escapeHtml(data.state)}`
      + (data.acc_yer ? ` &nbsp;·&nbsp; ${escapeHtml(data.acc_yer)}` : '');

    const headerCells = columns.map(c =>
      `<th title="${escapeHtml(c.header)}">${escapeHtml(c.header)}</th>`
    ).join('');

    const body = data.students.map((stu, i) => {
      const vals = getRowValues(s.sub, stu.ind);
      const cells = columns.map(c => {
        const v = vals[c.key];
        const txt = v === '' || v === undefined || v === null ? '' : escapeHtml(v);
        return `<td class="mark">${txt}</td>`;
      }).join('');
      return `<tr>
        <td class="num">${i + 1}</td>
        <td class="ind">${escapeHtml(stu.ind)}</td>
        <td class="name">${escapeHtml(stu.name)}</td>
        ${cells}
      </tr>`;
    }).join('');

    const teacherInfo = `<b>Teacher:</b> ${escapeHtml(s.teacher || '________________________')}`;

    return `<div class="rss-page">
      <table class="rss-print">
        <thead>
          <tr class="school-row"><td colspan="${totalCols}">${schoolName}</td></tr>
          <tr class="period-row"><td colspan="${totalCols}">${periodLine}</td></tr>
          <tr class="teacher-row"><td colspan="${totalCols}">${teacherInfo}</td></tr>
          <tr class="head-row">
            <th>#</th><th>Index</th><th>Student Name</th>${headerCells}
          </tr>
        </thead>
        <tbody>${body || `<tr><td colspan="${totalCols}" style="text-align:center;color:#666;padding:20mm 0">No students.</td></tr>`}</tbody>
      </table>
    </div>`;
  }).join('');
}

// Excel uses A1-style cell refs. Column index → letter (A, B, ...AA).
function colLetter(n) {
  let out = '';
  while (n > 0) { const r = (n - 1) % 26; out = String.fromCharCode(65 + r) + out; n = Math.floor((n - 1) / 26); }
  return out;
}

// XML attribute / text escape — XMLSS is XML, not HTML, so we also
// need to escape the apostrophe to &apos; for full compliance.
function xmlEscape(s) {
  return String(s ?? '').replace(/[&<>"']/g, c =>
    ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&apos;' }[c]));
}

// Sheet names: ≤31 chars, no /\?*[]:, disambiguate duplicates.
function uniqueSheetName(raw, used) {
  let base = String(raw || 'Sheet').replace(/[\/\\?*\[\]:]/g, ' ').trim().substring(0, 31) || 'Sheet';
  if (!used.has(base)) { used.add(base); return base; }
  let i = 2;
  let candidate;
  do {
    const suffix = ` (${i})`;
    candidate = base.substring(0, 31 - suffix.length) + suffix;
    i++;
  } while (used.has(candidate));
  used.add(candidate);
  return candidate;
}

// ────────────────────────────────────────────────────────────────
// Real .xlsx export (ExcelJS) — produces a proper Office Open XML
// workbook. One worksheet per subject (visible as a sheet tab at the
// bottom). Headers, colours, formulas and protection mirror the
// on-screen Raw Score Sheet exactly, so teachers can fill the file
// offline and the totals / grade / position update live in Excel.
// ────────────────────────────────────────────────────────────────

// Reuse the existing colLetter() — already defined above for the XML
// exporter. Here we wrap a small cellRef helper around it.
const cellRef = (row, col) => `${colLetter(col)}${row}`;

// Build the human-readable formula caption the on-screen sheet shows
// at the bottom — same wording, same structure, so the .xlsx feels
// identical to the dialog.
function buildFormulaCaption(profile, state) {
  if (profile?.mode === 'single') {
    const max   = Math.max(1, Number(profile.max_score) || 100);
    const scale = Math.max(1, Number(profile.scale_to)  || 100);
    return max === scale
      ? `Total = Score (out of ${max})`
      : `Total = (Score/${max})×${scale}`;
  }
  if (!profile || profile.mode === 'sba') {
    const comps = profile?.components || [];
    if (!comps.length) return 'Total = raw score';
    const parts = comps.map(c => {
      const n = Math.max(1, Number(c.item_count) || 1);
      const pm = Math.max(0.01, Number(c.max_mark_per_item) || 100);
      const w = Number(c.weight_pct) || 0;
      const inner = n === 1 ? `(${c.label}/${pm})` : `(avg(${c.label} ${n} items)/${pm})`;
      return `${inner}×${w}%`;
    });
    return 'Total = ' + parts.join(' + ');
  }
  if (state === 'Term Exam' || state === 'Terminal') {
    const cR = Number(profile.cl_res) || 60;
    const eR = Number(profile.ex_res) || 100;
    const cS = Number(profile.cl_sc)  || 50;
    const eS = Number(profile.exa_sc) || 50;
    return `Total = (Class Score/${cR})×${cS}% + (Exam Score/${eR})×${eS}%`;
  }
  return 'Total = raw score';
}

// Nested-IF band lookup against the school's grading scale — A1 style
// for the xlsx writer. Bands are sorted highest-first and each nested
// IF checks ONLY the lower bound, so decimals in the gap between
// consecutive integer bands (e.g. 79.87 between 70-79 and 80-100)
// fall into the correct band instead of returning "".
function buildA1BandFormula(totalRef, grades, field) {
  if (!grades || grades.length === 0) return '""';
  const sorted = [...grades].sort((a, b) => b.hi - a.hi);
  const esc = (v) => String(v ?? '').replace(/"/g, '""');
  let inner = '""';
  for (let i = sorted.length - 1; i >= 0; i--) {
    const g = sorted[i];
    inner = `IF(${totalRef}>=${g.lo},"${esc(g[field])}",${inner})`;
  }
  return `IF(ISNUMBER(${totalRef}),${inner},"")`;
}

// Position as "1st" / "2nd" / "11th"… instead of a bare integer.
function buildA1OrdinalRank(totalRef, totalRange) {
  const r = `RANK(${totalRef},${totalRange},0)`;
  return `IF(ISNUMBER(${totalRef}),${r}&IF(OR(MOD(${r},100)=11,MOD(${r},100)=12,MOD(${r},100)=13),"th",IFERROR(CHOOSE(MOD(${r},10),"st","nd","rd"),"th")),"")`;
}

// Build one worksheet (one subject = one Excel tab).
function addSubjectWorksheet(wb, sheetName, subject, data, columns, school, allGrades, formulaCaption, entries = {}) {
  const ws = wb.addWorksheet(sheetName, {
    pageSetup: {
      orientation: 'landscape',
      fitToPage: true, fitToWidth: 1, fitToHeight: 0,
      paperSize: 9,
      margins: { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 },
      horizontalCentered: true,
    },
    properties: { defaultRowHeight: 16 },
    views: [{ state: 'frozen', xSplit: 3, ySplit: 8, showGridLines: false }],
  });

  const fixedCols  = 3;
  const totalCols  = fixedCols + columns.length + 4;
  const totalCol   = fixedCols + columns.length + 1;
  const posCol     = totalCol + 1;
  const gradeCol   = totalCol + 2;
  const remarksCol = totalCol + 3;
  const dataStartRow = 9;
  const dataEndRow   = dataStartRow + Math.max(1, data.students.length) - 1;

  const widths = [5, 11, 28];
  for (let i = 0; i < columns.length; i++) widths.push(11);
  widths.push(10, 7, 9, 16);
  ws.columns = widths.map(w => ({ width: w }));

  const merged = (r1, c1, r2, c2, value, style, height) => {
    ws.mergeCells(r1, c1, r2, c2);
    if (height != null) ws.getRow(r1).height = height;
    const cell = ws.getCell(r1, c1);
    cell.value = value;
    cell.style = style;
  };

  const addressLine = [school.address, school.phone, school.email].filter(Boolean).join('  ·  ');
  const periodLine  = `Class: ${data.class}   ·   Term: ${(data.term || '').replace('_', ' ')}   ·   Exam: ${data.state}${data.acc_yer ? `   ·   Year: ${data.acc_yer}` : ''}`;

  merged(1, 1, 1, totalCols, school.school_name || 'School', {
    font: { name: 'Calibri', size: 14, bold: true, color: { argb: 'FF1A237E' } },
    alignment: { horizontal: 'center', vertical: 'middle' },
  }, 24);
  merged(2, 1, 2, totalCols, addressLine, {
    font: { italic: true, size: 9, color: { argb: 'FF555555' } },
    alignment: { horizontal: 'center', vertical: 'middle' },
  }, 14);
  merged(3, 1, 3, totalCols, String(subject.sub).toUpperCase(), {
    font: { name: 'Calibri', size: 13, bold: true, color: { argb: 'FF1A237E' } },
    alignment: { horizontal: 'center', vertical: 'middle' },
  }, 22);
  merged(4, 1, 4, totalCols, periodLine, {
    font: { size: 10, color: { argb: 'FF333333' } },
    alignment: { horizontal: 'center', vertical: 'middle' },
  }, 16);
  merged(5, 1, 5, totalCols, `Teacher: ${subject.teacher || '________________________'}`, {
    font: { size: 10, bold: true, color: { argb: 'FF333333' } },
    alignment: { horizontal: 'center', vertical: 'middle' },
  }, 16);
  ws.getRow(6).height = 6;

  const headerBorder = {
    top:    { style: 'thin', color: { argb: 'FF1A237E' } },
    left:   { style: 'thin', color: { argb: 'FF5C6BC0' } },
    right:  { style: 'thin', color: { argb: 'FF5C6BC0' } },
    bottom: { style: 'thin', color: { argb: 'FF5C6BC0' } },
  };
  const mkHeaderStyle = (fillArgb) => ({
    font: { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: fillArgb } },
    alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
    border: headerBorder,
  });
  const FIXED_FILL = 'FF3949AB';
  ws.getRow(7).height = 28;
  const setHeader = (c, v, fill) => {
    const cell = ws.getCell(7, c);
    cell.value = v;
    cell.style = mkHeaderStyle(fill);
  };
  setHeader(1, '#', FIXED_FILL);
  setHeader(2, 'INDEX', FIXED_FILL);
  setHeader(3, 'STUDENT NAME', FIXED_FILL);
  for (let i = 0; i < columns.length; i++) setHeader(fixedCols + 1 + i, columns[i].header, FIXED_FILL);
  setHeader(totalCol,   'TOTAL',   'FF1565C0');
  setHeader(posCol,     'POS',     'FF2E7D32');
  setHeader(gradeCol,   'GRADE',   'FF6A1B9A');
  setHeader(remarksCol, 'REMARKS', 'FFAD1457');

  ws.getRow(8).height = 16;
  const maxLabelStyle = {
    font: { italic: true, size: 9, color: { argb: 'FF666666' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } },
    alignment: { horizontal: 'right', vertical: 'middle' },
    border: headerBorder,
  };
  const maxCellStyle = {
    font: { italic: true, size: 9, color: { argb: 'FF666666' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } },
    alignment: { horizontal: 'center', vertical: 'middle' },
    border: headerBorder,
  };
  ws.mergeCells(8, 1, 8, 3);
  ws.getCell(8, 1).value = 'Max per item →';
  ws.getCell(8, 1).style = maxLabelStyle;
  for (let i = 0; i < columns.length; i++) {
    const cell = ws.getCell(8, fixedCols + 1 + i);
    cell.value = Number(columns[i].max) || 100;
    cell.style = maxCellStyle;
  }
  ws.getCell(8, totalCol).value = 100;
  ws.getCell(8, totalCol).style = maxCellStyle;
  ws.getCell(8, posCol).style = maxCellStyle;
  ws.getCell(8, gradeCol).style = maxCellStyle;
  ws.getCell(8, remarksCol).style = maxCellStyle;

  const totalRangeAbs = `$${colLetter(totalCol)}$${dataStartRow}:$${colLetter(totalCol)}$${dataEndRow}`;
  const baseBorder = {
    top:    { style: 'thin', color: { argb: 'FFE0E0E0' } },
    left:   { style: 'thin', color: { argb: 'FFE0E0E0' } },
    right:  { style: 'thin', color: { argb: 'FFE0E0E0' } },
    bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
  };
  const stripeFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFAFA' } };

  for (let i = 0; i < data.students.length; i++) {
    const stu = data.students[i];
    const rowNum = dataStartRow + i;
    ws.getRow(rowNum).height = 19;
    const stripe = (i % 2 === 1);
    const baseRowFill = stripe ? stripeFill : undefined;
    const mk = (col, value, font, align, fill) => {
      const cell = ws.getCell(rowNum, col);
      cell.value = value;
      cell.style = {
        font,
        alignment: { vertical: 'middle', horizontal: align },
        border: baseBorder,
        ...(fill ? { fill } : {}),
        protection: { locked: true },
      };
    };
    mk(1, i + 1,         { size: 9, color: { argb: 'FF777777' } },                     'right', baseRowFill);
    mk(2, stu.ind,       { name: 'Consolas', size: 9 },                                'left',  baseRowFill);
    mk(3, stu.name,      { bold: true, size: 10 },                                     'left',  baseRowFill);

    // Pre-fill any mark cells the teacher has already entered. The
    // value lives in `entries["{sub}|{ind}"][col.key]` and may be a
    // string (typed) or a number (saved). The TOTAL / POS / GRADE /
    // REMARKS formulas reference these cells so they recompute
    // immediately when Excel opens the file.
    const rowEntries = entries[`${subject.sub}|${stu.ind}`] || {};
    for (let ci = 0; ci < columns.length; ci++) {
      const max = Math.max(0.01, Number(columns[ci].max) || 100);
      const cell = ws.getCell(rowNum, fixedCols + 1 + ci);
      const raw = rowEntries[columns[ci].key];
      const num = (raw === '' || raw === null || raw === undefined) ? null : Number(raw);
      cell.value = (num !== null && Number.isFinite(num)) ? num : null;
      cell.style = {
        font: { size: 10 },
        alignment: { vertical: 'middle', horizontal: 'right' },
        border: baseBorder,
        protection: { locked: false },
        ...(baseRowFill ? { fill: baseRowFill } : {}),
      };
      cell.dataValidation = {
        type: 'decimal', operator: 'between',
        formulae: [0, max],
        showErrorMessage: true, errorStyle: 'warning',
        errorTitle: 'Over max', error: `Enter a value between 0 and ${max}.`,
      };
    }

    const rowMarkRange = columns.length
      ? `${cellRef(rowNum, fixedCols + 1)}:${cellRef(rowNum, fixedCols + columns.length)}`
      : null;
    const emptyGuard = rowMarkRange ? `COUNT(${rowMarkRange})=0` : 'TRUE';
    let totalBody;
    if (data.profile?.mode === 'sba') {
      const groups = new Map();
      columns.forEach((c, ci) => {
        if (c.kind !== 'sba') return;
        if (!groups.has(c.component)) {
          groups.set(c.component, { idxs: [], w: c.weight_pct, m: Math.max(0.01, Number(c.max) || 100) });
        }
        groups.get(c.component).idxs.push(ci);
      });
      const parts = [];
      for (const { idxs, w, m } of groups.values()) {
        const refs = idxs.map(ci => cellRef(rowNum, fixedCols + 1 + ci));
        parts.push(`IFERROR(AVERAGE(${refs.join(',')})/${m}*${w},0)`);
      }
      totalBody = `ROUND(${parts.join('+') || '0'},2)`;
    } else if (data.state === 'Term Exam' || data.state === 'Terminal') {
      const c = columns.find(x => x.kind === 'simple-class');
      const e = columns.find(x => x.kind === 'simple-exam');
      const ci = c ? columns.indexOf(c) : -1;
      const ei = e ? columns.indexOf(e) : -1;
      const parts = [];
      if (ci >= 0) parts.push(`IFERROR(${cellRef(rowNum, fixedCols + 1 + ci)}/${c.denom}*${c.weight_pct},0)`);
      if (ei >= 0) parts.push(`IFERROR(${cellRef(rowNum, fixedCols + 1 + ei)}/${e.denom}*${e.weight_pct},0)`);
      totalBody = `ROUND(${parts.join('+') || '0'},2)`;
    } else {
      totalBody = cellRef(rowNum, fixedCols + 1);
    }
    const totalFormula = `IF(${emptyGuard},"",${totalBody})`;
    const totalCellRef = cellRef(rowNum, totalCol);

    const setComputed = (col, formula, fontColor, fillArgb, align, italic = false) => {
      const cell = ws.getCell(rowNum, col);
      cell.value = { formula };
      cell.style = {
        font: { bold: true, italic, color: { argb: fontColor }, size: 10 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: fillArgb } },
        alignment: { horizontal: align, vertical: 'middle' },
        border: baseBorder,
        protection: { locked: true },
      };
    };
    setComputed(totalCol,   totalFormula,                                                'FF0D47A1', 'FFE3F2FD', 'right');
    ws.getCell(rowNum, totalCol).numFmt = '0.00';
    setComputed(posCol,     buildA1OrdinalRank(totalCellRef, totalRangeAbs),             'FF1B5E20', 'FFE8F5E9', 'center');
    setComputed(gradeCol,   buildA1BandFormula(totalCellRef, allGrades, 'grade'),        'FF4A148C', 'FFF3E5F5', 'center');
    setComputed(remarksCol, buildA1BandFormula(totalCellRef, allGrades, 'remarks'),      'FF880E4F', 'FFFCE4EC', 'center', true);
  }

  if (data.students.length === 0) {
    const r = dataStartRow;
    ws.mergeCells(r, 1, r, totalCols);
    ws.getCell(r, 1).value = 'No students registered.';
    ws.getCell(r, 1).style = {
      font: { italic: true, color: { argb: 'FF999999' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
    };
  }

  const captionRow = dataEndRow + 2;
  ws.mergeCells(captionRow, 1, captionRow, totalCols);
  ws.getRow(captionRow).height = 18;
  ws.getCell(captionRow, 1).value = formulaCaption;
  ws.getCell(captionRow, 1).style = {
    font: { italic: true, size: 9, color: { argb: 'FF555555' } },
    alignment: { horizontal: 'center', vertical: 'middle' },
  };

  // ── Grading Scale footer ──────────────────────────────────────
  // Replaces the old MERIT block — list the exact grade bands the
  // GRADE and REMARKS columns above use. Falls back to the WAEC
  // default scale only when the school hasn't customised it in
  // Examination Settings.
  const scaleTitleRow = captionRow + 2;
  ws.mergeCells(scaleTitleRow, 1, scaleTitleRow, totalCols);
  ws.getRow(scaleTitleRow).height = 22;
  ws.getCell(scaleTitleRow, 1).value = 'GRADING SCALE';
  ws.getCell(scaleTitleRow, 1).style = {
    font: { bold: true, size: 11, color: { argb: 'FF1A237E' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EAF6' } },
    alignment: { horizontal: 'center', vertical: 'middle' },
    border: { top: { style: 'medium', color: { argb: 'FF3949AB' } } },
  };

  // Anchor the legend under the GRADE / REMARKS columns so a reader
  // can scan straight from a student's grade letter to its meaning.
  const legendStartCol  = Math.max(1, totalCols - 5);
  const legendGradeCol  = legendStartCol;
  const legendMarksCol  = legendStartCol + 1;
  const legendRemStart  = legendStartCol + 2;
  const legendRemEnd    = totalCols;

  const legendHeaderRow = scaleTitleRow + 1;
  ws.getRow(legendHeaderRow).height = 20;
  const legendHeaderStyle = (fillArgb) => ({
    font: { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: fillArgb } },
    alignment: { horizontal: 'center', vertical: 'middle' },
    border: headerBorder,
  });
  ws.getCell(legendHeaderRow, legendGradeCol).value = 'GRADE';
  ws.getCell(legendHeaderRow, legendGradeCol).style = legendHeaderStyle('FF6A1B9A');
  ws.getCell(legendHeaderRow, legendMarksCol).value = 'MARKS';
  ws.getCell(legendHeaderRow, legendMarksCol).style = legendHeaderStyle('FF1565C0');
  ws.mergeCells(legendHeaderRow, legendRemStart, legendHeaderRow, legendRemEnd);
  ws.getCell(legendHeaderRow, legendRemStart).value = 'REMARKS';
  ws.getCell(legendHeaderRow, legendRemStart).style = legendHeaderStyle('FFAD1457');

  // Sort highest band first, so the legend reads top-down.
  const sortedBands = [...allGrades].sort((a, b) => b.hi - a.hi);
  sortedBands.forEach((g, i) => {
    const r = legendHeaderRow + 1 + i;
    ws.getRow(r).height = 18;
    const stripe = i % 2 === 1;
    const fill = stripe ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFAFA' } } : null;
    const mkLegendCell = (fontColor, align, italic = false) => ({
      font: { bold: true, italic, size: 10, color: { argb: fontColor } },
      alignment: { horizontal: align, vertical: 'middle' },
      border: baseBorder,
      ...(fill ? { fill } : {}),
    });
    ws.getCell(r, legendGradeCol).value = g.grade;
    ws.getCell(r, legendGradeCol).style = mkLegendCell('FF4A148C', 'center');
    ws.getCell(r, legendMarksCol).value = `${g.lo} – ${g.hi}`;
    ws.getCell(r, legendMarksCol).style = mkLegendCell('FF0D47A1', 'center');
    ws.mergeCells(r, legendRemStart, r, legendRemEnd);
    ws.getCell(r, legendRemStart).value = g.remarks;
    ws.getCell(r, legendRemStart).style = mkLegendCell('FF880E4F', 'center', true);
  });

  ws.protect('', {
    selectLockedCells: true,
    selectUnlockedCells: true,
    formatCells: false, formatColumns: false, formatRows: false,
    insertRows: false, deleteRows: false,
  });
}

async function buildXlsxBuffer(data, columns, school = {}, entries = {}) {
  const wb = new ExcelJS.Workbook();
  wb.creator = school.school_name || 'School System';
  wb.created = new Date();
  if (!data?.subjects?.length) {
    const ws = wb.addWorksheet('No subjects');
    ws.getCell('A1').value = 'No subjects to export.';
    return wb.xlsx.writeBuffer();
  }
  const allGrades = (data.grades && data.grades.length > 0 ? data.grades : DEFAULT_GRADES).map(g => {
    const a = Number(g.marks_1); const b = Number(g.marks_2);
    const A = Number.isFinite(a) ? a : 0; const B = Number.isFinite(b) ? b : 0;
    return { lo: Math.min(A, B), hi: Math.max(A, B),
             grade: String(g.grade ?? ''), remarks: String(g.remarks ?? '') };
  });
  const formulaCaption = buildFormulaCaption(data.profile, data.state);
  const usedNames = new Set();
  for (const s of data.subjects) {
    addSubjectWorksheet(wb, uniqueSheetName(s.sub, usedNames),
      s, data, columns, school, allGrades, formulaCaption, entries);
  }
  return wb.xlsx.writeBuffer();
}

function downloadXlsx(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Build the Excel XML 2003 Spreadsheet for the Raw Score Sheet.
//
//   • One Worksheet (= tab) per subject — teachers click "English",
//     "Maths" etc. at the bottom of the workbook rather than scrolling.
//   • Sheet protection ON; only the mark-entry cells are unlocked.
//     Name, Index, Total, Position, Grade and Remarks are read-only.
//   • Formulas are stored as ss:Formula in R1C1 notation (the XMLSS
//     spec's default reference style) so Excel always recognises them
//     and recalculates immediately when a mark is typed.
//   • Per-cell styling is defined in <Styles> and referenced by ID —
//     the school banner, period band, headers, max-row, data cells
//     and merit rows all carry the polished colour scheme.
function xmlForExcel(data, columns, school = {}) {
  if (!data?.subjects?.length) {
    return `<?xml version="1.0"?>\n<?mso-application progid="Excel.Sheet"?>\n` +
      `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"\n` +
      ` xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n` +
      `<Worksheet ss:Name="No subjects"><Table><Row><Cell><Data ss:Type="String">No subjects.</Data></Cell></Row></Table></Worksheet>\n` +
      `</Workbook>`;
  }

  // Pull the school's grading bands from the server payload; only fall
  // back to DEFAULT_GRADES (Ghana SBA scale) if the table is empty.
  // MIN/MAX normalises away the "did the admin enter the high or the
  // low into marks_1?" ambiguity.
  const allGrades = (data.grades && data.grades.length > 0 ? data.grades : DEFAULT_GRADES)
    .map(g => {
      const a = Number(g.marks_1);
      const b = Number(g.marks_2);
      const A = Number.isFinite(a) ? a : 0;
      const B = Number.isFinite(b) ? b : 0;
      return {
        lo: Math.min(A, B),
        hi: Math.max(A, B),
        grade:   String(g.grade   ?? ''),
        remarks: String(g.remarks ?? ''),
      };
    });

  const usedNames = new Set();
  const sheets = data.subjects.map(s =>
    buildXmlWorksheet(s, uniqueSheetName(s.sub, usedNames), data, columns, school, allGrades)
  ).join('\n');

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <DocumentProperties xmlns="urn:schemas-microsoft-com:office:office">
  <Title>${xmlEscape(`Score Sheet — ${data.class} · ${data.term} · ${data.state}`)}</Title>
  <Author>${xmlEscape(school.school_name || 'School System')}</Author>
 </DocumentProperties>
 <ExcelWorkbook xmlns="urn:schemas-microsoft-com:office:excel">
  <ActiveSheet>0</ActiveSheet>
  <ProtectStructure>False</ProtectStructure>
  <ProtectWindows>False</ProtectWindows>
 </ExcelWorkbook>
${XMLSS_STYLES}
${sheets}
</Workbook>`;
}

// All cell styles used across the workbook. Defined once at workbook
// level and referenced by ID on each <Cell>. Every style that should
// stay read-only carries <Protection ss:Protected="1"/>; only MarkCell
// (the actual entry cells) is Protected="0" so teachers can type into
// it once sheet protection is on.
const XMLSS_STYLES = `<Styles>
  <Style ss:ID="Default" ss:Name="Normal">
   <Alignment ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="10"/>
   <Protection ss:Protected="1"/>
  </Style>

  <Style ss:ID="SchoolBanner">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="20" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#1A237E" ss:Pattern="Solid"/>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="AddressBanner">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="9.5" ss:Italic="1" ss:Color="#C5CAE9"/>
   <Interior ss:Color="#1A237E" ss:Pattern="Solid"/>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="PeriodBanner">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="12" ss:Bold="1" ss:Color="#1A237E"/>
   <Interior ss:Color="#E8EAF6" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#9FA8DA"/>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#9FA8DA"/>
   </Borders>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="TeacherCell">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#37474F"/>
   <Interior ss:Color="#FAFBFC" ss:Pattern="Solid"/>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="SignatureCell">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#37474F"/>
   <Interior ss:Color="#FAFBFC" ss:Pattern="Solid"/>
   <Protection ss:Protected="1"/>
  </Style>

  <Style ss:ID="HeaderBase">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/>
   <Font ss:FontName="Calibri" ss:Size="9.5" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#3949AB" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1A237E"/>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1A237E"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1A237E"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1A237E"/>
   </Borders>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="HeaderName">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="9.5" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#3949AB" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1A237E"/>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1A237E"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1A237E"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1A237E"/>
   </Borders>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="HeaderTotal">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="9.5" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#1565C0" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#0D47A1"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#0D47A1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#0D47A1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#0D47A1"/></Borders>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="HeaderPosition">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="9.5" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#2E7D32" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1B5E20"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1B5E20"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1B5E20"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1B5E20"/></Borders>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="HeaderGrade">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="9.5" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#6A1B9A" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#4A148C"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#4A148C"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#4A148C"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#4A148C"/></Borders>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="HeaderRemarks">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="9.5" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#AD1457" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#880E4F"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#880E4F"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#880E4F"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#880E4F"/></Borders>
   <Protection ss:Protected="1"/>
  </Style>

  <Style ss:ID="MaxRow">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="9" ss:Bold="1" ss:Color="#5D4037"/>
   <Interior ss:Color="#FFF8E1" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7CCC8"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7CCC8"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7CCC8"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7CCC8"/></Borders>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="MaxRowLabel">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="9" ss:Italic="1" ss:Color="#5D4037"/>
   <Interior ss:Color="#FFF8E1" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7CCC8"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7CCC8"/></Borders>
   <Protection ss:Protected="1"/>
  </Style>

  <Style ss:ID="RowNum">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="9" ss:Color="#777777"/>
   <Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CFD8DC"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CFD8DC"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CFD8DC"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CFD8DC"/></Borders>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="IndexCell">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Consolas" ss:Size="10" ss:Color="#37474F"/>
   <Interior ss:Color="#ECEFF1" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CFD8DC"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CFD8DC"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CFD8DC"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CFD8DC"/></Borders>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="NameCell">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#212121"/>
   <Interior ss:Color="#ECEFF1" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CFD8DC"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CFD8DC"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CFD8DC"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CFD8DC"/></Borders>
   <Protection ss:Protected="1"/>
  </Style>

  <!-- THE ONLY UNLOCKED CELL — soft yellow tint hints to teachers
       that this is where they type marks. -->
  <Style ss:ID="MarkCell">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#1A237E"/>
   <Interior ss:Color="#FFFDE7" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFE082"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFE082"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFE082"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFE082"/></Borders>
   <NumberFormat ss:Format="General"/>
   <Protection ss:Protected="0"/>
  </Style>

  <Style ss:ID="TotalCell">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#0D47A1"/>
   <Interior ss:Color="#E3F2FD" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#90CAF9"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#90CAF9"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#90CAF9"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#90CAF9"/></Borders>
   <NumberFormat ss:Format="0.00"/>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="PositionCell">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#1B5E20"/>
   <Interior ss:Color="#E8F5E9" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#A5D6A7"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#A5D6A7"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#A5D6A7"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#A5D6A7"/></Borders>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="GradeCell">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#4A148C"/>
   <Interior ss:Color="#F3E5F5" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CE93D8"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CE93D8"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CE93D8"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CE93D8"/></Borders>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="RemarksCell">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="9.5" ss:Italic="1" ss:Color="#880E4F"/>
   <Interior ss:Color="#FCE4EC" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#F48FB1"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#F48FB1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#F48FB1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#F48FB1"/></Borders>
   <Protection ss:Protected="1"/>
  </Style>

  <Style ss:ID="MeritTitle">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#1A237E" ss:Pattern="Solid"/>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="MeritLabel">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#263238"/>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="MeritLabelStripe">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#263238"/>
   <Interior ss:Color="#F5F5FA" ss:Pattern="Solid"/>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="MeritValueBlue">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#0D47A1"/>
   <Interior ss:Color="#E3F2FD" ss:Pattern="Solid"/>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="MeritValueGreen">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#1B5E20"/>
   <Interior ss:Color="#E8F5E9" ss:Pattern="Solid"/>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="MeritValueOrange">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#E65100"/>
   <Interior ss:Color="#FFF3E0" ss:Pattern="Solid"/>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="MeritValueRed">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#B71C1C"/>
   <Interior ss:Color="#FFEBEE" ss:Pattern="Solid"/>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="MeritValueDefault">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#0D47A1"/>
   <Interior ss:Color="#F5F5FA" ss:Pattern="Solid"/>
   <Protection ss:Protected="1"/>
  </Style>
  <Style ss:ID="MeritTopPerformer">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="10.5" ss:Bold="1" ss:Color="#4A148C"/>
   <Interior ss:Color="#F5F5FA" ss:Pattern="Solid"/>
   <Protection ss:Protected="1"/>
  </Style>
 </Styles>`;

// Excel ordinal — wraps a RANK formula so the displayed result is
// "1st", "2nd", "11th", "23rd"… instead of a bare integer.
function buildOrdinalRankFormula(totalRef, totalRange) {
  const r = `RANK(${totalRef},${totalRange},0)`;
  return `=IF(ISNUMBER(${totalRef}),${r}&IF(OR(MOD(${r},100)=11,MOD(${r},100)=12,MOD(${r},100)=13),"th",IFERROR(CHOOSE(MOD(${r},10),"st","nd","rd"),"th")),"")`;
}

// Build one Worksheet (one subject = one Excel tab).
function buildXmlWorksheet(s, sheetName, data, columns, school, allGrades) {
  const fixedCols      = 3;
  const totalColIdx    = fixedCols + columns.length + 1;
  const positionColIdx = fixedCols + columns.length + 2;
  const totalCols      = fixedCols + columns.length + 4;
  const dataStartRow   = 8;
  const dataEndRow     = dataStartRow + Math.max(1, data.students.length) - 1;

  // R1C1 absolute ranges — the XMLSS spec's default reference style.
  const rc        = (r, c) => `R${r}C${c}`;
  const rcRange   = (r1, c1, r2, c2) => `${rc(r1, c1)}:${rc(r2, c2)}`;
  const totalRange    = rcRange(dataStartRow, totalColIdx, dataEndRow, totalColIdx);
  const positionRange = rcRange(dataStartRow, positionColIdx, dataEndRow, positionColIdx);

  // ── Column widths ──
  let columnDefs = '';
  columnDefs += `<Column ss:Width="32"/>`;
  columnDefs += `<Column ss:Width="78"/>`;
  columnDefs += `<Column ss:Width="170"/>`;
  for (let i = 0; i < columns.length; i++) columnDefs += `<Column ss:Width="62"/>`;
  columnDefs += `<Column ss:Width="62"/>`;
  columnDefs += `<Column ss:Width="48"/>`;
  columnDefs += `<Column ss:Width="54"/>`;
  columnDefs += `<Column ss:Width="96"/>`;

  // ── Top banner ──
  const schoolName = school.school_name || 'School';
  const address    = [school.address, school.phone, school.email].filter(Boolean).join('  ·  ');
  const periodLine = `${s.sub}    ·    ${data.class}    ·    ${(data.term || '').replace(/_/g, ' ')}    ·    ${data.state}${data.acc_yer ? `    ·    ${data.acc_yer}` : ''}`;

  let rows = '';
  rows += `<Row ss:Height="34" ss:AutoFitHeight="0">
    <Cell ss:MergeAcross="${totalCols - 1}" ss:StyleID="SchoolBanner"><Data ss:Type="String">${xmlEscape(schoolName)}</Data></Cell>
  </Row>`;
  rows += `<Row ss:Height="18" ss:AutoFitHeight="0">
    <Cell ss:MergeAcross="${totalCols - 1}" ss:StyleID="AddressBanner"><Data ss:Type="String">${xmlEscape(address)}</Data></Cell>
  </Row>`;
  rows += `<Row ss:Height="24" ss:AutoFitHeight="0">
    <Cell ss:MergeAcross="${totalCols - 1}" ss:StyleID="PeriodBanner"><Data ss:Type="String">${xmlEscape(periodLine)}</Data></Cell>
  </Row>`;
  const halfL = Math.ceil(totalCols / 2);
  const halfR = totalCols - halfL;
  rows += `<Row ss:Height="20" ss:AutoFitHeight="0">
    <Cell ss:MergeAcross="${halfL - 1}" ss:StyleID="TeacherCell"><Data ss:Type="String">Teacher:   ${xmlEscape(s.teacher || '________________________')}</Data></Cell>
    <Cell ss:MergeAcross="${halfR - 1}" ss:StyleID="SignatureCell"><Data ss:Type="String">Signature:   ________________________</Data></Cell>
  </Row>`;
  rows += `<Row ss:Height="6" ss:AutoFitHeight="0"/>`;

  // ── Header row ──
  let headerCells = '';
  headerCells += `<Cell ss:StyleID="HeaderBase"><Data ss:Type="String">#</Data></Cell>`;
  headerCells += `<Cell ss:StyleID="HeaderBase"><Data ss:Type="String">Index</Data></Cell>`;
  headerCells += `<Cell ss:StyleID="HeaderName"><Data ss:Type="String">Student Name</Data></Cell>`;
  for (const c of columns) {
    headerCells += `<Cell ss:StyleID="HeaderBase"><Data ss:Type="String">${xmlEscape(c.header)}</Data></Cell>`;
  }
  headerCells += `<Cell ss:StyleID="HeaderTotal"><Data ss:Type="String">Total</Data></Cell>`;
  headerCells += `<Cell ss:StyleID="HeaderPosition"><Data ss:Type="String">Pos</Data></Cell>`;
  headerCells += `<Cell ss:StyleID="HeaderGrade"><Data ss:Type="String">Grade</Data></Cell>`;
  headerCells += `<Cell ss:StyleID="HeaderRemarks"><Data ss:Type="String">Remarks</Data></Cell>`;
  rows += `<Row ss:Height="30" ss:AutoFitHeight="0">${headerCells}</Row>`;

  // ── Max-per-item row ──
  let maxCells = '';
  maxCells += `<Cell ss:MergeAcross="2" ss:StyleID="MaxRowLabel"><Data ss:Type="String">Max per item →</Data></Cell>`;
  for (const c of columns) {
    maxCells += `<Cell ss:StyleID="MaxRow"><Data ss:Type="Number">${Number(c.max) || 100}</Data></Cell>`;
  }
  maxCells += `<Cell ss:StyleID="MaxRow"><Data ss:Type="Number">100</Data></Cell>`;
  maxCells += `<Cell ss:StyleID="MaxRow"/>`;
  maxCells += `<Cell ss:StyleID="MaxRow"/>`;
  maxCells += `<Cell ss:StyleID="MaxRow"/>`;
  rows += `<Row ss:Height="20" ss:AutoFitHeight="0">${maxCells}</Row>`;

  // ── Student data rows ──
  if (data.students.length === 0) {
    rows += `<Row><Cell ss:MergeAcross="${totalCols - 1}" ss:StyleID="MeritLabel"><Data ss:Type="String">No students registered.</Data></Cell></Row>`;
  } else {
    for (let i = 0; i < data.students.length; i++) {
      const stu = data.students[i];
      const rowNum = dataStartRow + i;

      // Over-max guard expressed against each mark cell.
      const overChecks = columns.map((c, ci) => {
        const max = Math.max(0.01, Number(c.max) || 100);
        return `${rc(rowNum, fixedCols + ci + 1)}>${max}`;
      });
      const overGuard = overChecks.length ? `OR(${overChecks.join(',')})` : 'FALSE';

      // Range covering every mark cell on this row — used to detect a
      // row where the teacher hasn't typed anything yet so we can
      // return "" instead of misclassifying 0 as F9 / Fail.
      const rowMarkRange = columns.length
        ? rcRange(rowNum, fixedCols + 1, rowNum, fixedCols + columns.length)
        : null;
      const emptyGuard = rowMarkRange ? `COUNT(${rowMarkRange})=0` : 'TRUE';

      // Total formula by profile mode.
      let totalBody;
      if (data.profile?.mode === 'sba') {
        const groups = new Map();
        columns.forEach((c, ci) => {
          if (c.kind !== 'sba') return;
          if (!groups.has(c.component)) {
            groups.set(c.component, { idxs: [], w: c.weight_pct, m: Math.max(0.01, Number(c.max) || 100) });
          }
          groups.get(c.component).idxs.push(ci);
        });
        const parts = [];
        for (const { idxs, w, m } of groups.values()) {
          const cells = idxs.map(ci => rc(rowNum, fixedCols + ci + 1));
          parts.push(`IFERROR(AVERAGE(${cells.join(',')})/${m}*${w},0)`);
        }
        totalBody = `ROUND(${parts.join('+') || '0'},2)`;
      } else if (data.state === 'Term Exam' || data.state === 'Terminal') {
        const c = columns.find(x => x.kind === 'simple-class');
        const e = columns.find(x => x.kind === 'simple-exam');
        const ci = c ? columns.indexOf(c) : -1;
        const ei = e ? columns.indexOf(e) : -1;
        const cRef = ci >= 0 ? rc(rowNum, fixedCols + ci + 1) : '';
        const eRef = ei >= 0 ? rc(rowNum, fixedCols + ei + 1) : '';
        const parts = [];
        if (cRef) parts.push(`IFERROR(${cRef}/${c.denom}*${c.weight_pct},0)`);
        if (eRef) parts.push(`IFERROR(${eRef}/${e.denom}*${e.weight_pct},0)`);
        totalBody = `ROUND(${parts.join('+') || '0'},2)`;
      } else {
        const ref = rc(rowNum, fixedCols + 1);
        totalBody = ref;
      }
      const totalFormula = `=IF(${emptyGuard},"",IF(${overGuard},"OVER MAX",${totalBody}))`;

      const totalRef = rc(rowNum, totalColIdx);
      const positionFormula = buildOrdinalRankFormula(totalRef, totalRange);
      const gradeFormula    = buildBandFormula(totalRef, allGrades, 'grade');
      const remarksFormula  = buildBandFormula(totalRef, allGrades, 'remarks');

      let dataCells = '';
      dataCells += `<Cell ss:StyleID="RowNum"><Data ss:Type="Number">${i + 1}</Data></Cell>`;
      dataCells += `<Cell ss:StyleID="IndexCell"><Data ss:Type="String">${xmlEscape(stu.ind)}</Data></Cell>`;
      dataCells += `<Cell ss:StyleID="NameCell"><Data ss:Type="String">${xmlEscape(stu.name)}</Data></Cell>`;
      for (let ci = 0; ci < columns.length; ci++) {
        dataCells += `<Cell ss:StyleID="MarkCell"/>`;
      }
      dataCells += `<Cell ss:StyleID="TotalCell" ss:Formula="${xmlEscape(totalFormula)}"/>`;
      dataCells += `<Cell ss:StyleID="PositionCell" ss:Formula="${xmlEscape(positionFormula)}"/>`;
      dataCells += `<Cell ss:StyleID="GradeCell" ss:Formula="${xmlEscape(gradeFormula)}"/>`;
      dataCells += `<Cell ss:StyleID="RemarksCell" ss:Formula="${xmlEscape(remarksFormula)}"/>`;
      rows += `<Row ss:Height="22" ss:AutoFitHeight="0">${dataCells}</Row>`;
    }
  }

  // ── Spacer + MERIT footer ──
  rows += `<Row ss:Height="10" ss:AutoFitHeight="0"/>`;
  rows += `<Row ss:Height="26" ss:AutoFitHeight="0">
    <Cell ss:MergeAcross="${totalCols - 1}" ss:StyleID="MeritTitle"><Data ss:Type="String">MERIT — class performance (auto-calculated)</Data></Cell>
  </Row>`;

  const meritLabelSpan = totalCols - 4;
  const meritRow = (label, formula, valueStyle, stripe) => {
    const labelStyle = stripe ? 'MeritLabelStripe' : 'MeritLabel';
    let cells = '';
    cells += `<Cell ss:MergeAcross="${meritLabelSpan - 1}" ss:StyleID="${labelStyle}"><Data ss:Type="String">${xmlEscape(label)}</Data></Cell>`;
    cells += `<Cell ss:StyleID="${valueStyle}" ss:Formula="${xmlEscape(formula)}"/>`;
    cells += `<Cell ss:StyleID="${labelStyle}"/>`;
    cells += `<Cell ss:StyleID="${labelStyle}"/>`;
    cells += `<Cell ss:StyleID="${labelStyle}"/>`;
    return `<Row ss:Height="20" ss:AutoFitHeight="0">${cells}</Row>`;
  };

  rows += meritRow('Students with marks entered',  `=COUNT(${totalRange})`,                              'MeritValueDefault', true);
  rows += meritRow('Highest score',                 `=IFERROR(MAX(${totalRange}),"")`,                   'MeritValueGreen',   false);
  rows += meritRow('Lowest score',                  `=IFERROR(MIN(${totalRange}),"")`,                   'MeritValueOrange',  true);
  rows += meritRow('Class average',                 `=IFERROR(ROUND(AVERAGE(${totalRange}),2),"")`,      'MeritValueBlue',    false);
  rows += meritRow('Pass count (>= 50)',            `=COUNTIF(${totalRange},">=50")`,                    'MeritValueGreen',   true);
  rows += meritRow('Fail count (< 50)',             `=COUNTIF(${totalRange},"<50")`,                     'MeritValueRed',     false);

  // Top performer — value spans the 4 trailing columns. Matches by
  // the highest Total (positions are now text "1st", "2nd"… so we
  // can't MATCH against a number any more).
  const nameRange = rcRange(dataStartRow, 3, dataEndRow, 3);
  const topPerfFormula = `=IFERROR(INDEX(${nameRange},MATCH(MAX(${totalRange}),${totalRange},0)),"")`;
  rows += `<Row ss:Height="22" ss:AutoFitHeight="0">
    <Cell ss:MergeAcross="${meritLabelSpan - 1}" ss:StyleID="MeritLabelStripe"><Data ss:Type="String">Top performer (rank 1)</Data></Cell>
    <Cell ss:MergeAcross="3" ss:StyleID="MeritTopPerformer" ss:Formula="${xmlEscape(topPerfFormula)}"/>
  </Row>`;

  // ss:Protected="1" on Worksheet plus <ProtectContents>True</...>
  // belt-and-braces so different Excel versions all respect the lock.
  return `<Worksheet ss:Name="${xmlEscape(sheetName)}" ss:Protected="1">
  <Table ss:DefaultColumnWidth="60" ss:DefaultRowHeight="14">
${columnDefs}
${rows}
  </Table>
  <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
   <PageSetup>
    <Layout x:Orientation="Landscape" x:CenterHorizontal="1"/>
    <PageMargins x:Bottom="0.4" x:Left="0.4" x:Right="0.4" x:Top="0.4"/>
   </PageSetup>
   <FitToPage/>
   <Print>
    <ValidPrinterInfo/>
    <PaperSizeIndex>9</PaperSizeIndex>
    <HorizontalResolution>600</HorizontalResolution>
    <VerticalResolution>600</VerticalResolution>
   </Print>
   <ProtectObjects>False</ProtectObjects>
   <ProtectScenarios>False</ProtectScenarios>
   <ProtectContents>True</ProtectContents>
   <FreezePanes/>
   <FrozenNoSplit/>
   <SplitHorizontal>7</SplitHorizontal>
   <TopRowBottomPane>7</TopRowBottomPane>
   <ActivePane>2</ActivePane>
  </WorksheetOptions>
 </Worksheet>`;
}

// Build a nested-IF "band lookup" formula that returns the grade or
// remark for a given Total. Each band is normalised with MIN/MAX so
// we don't care which column the admin treated as "from" or "to".
// Bands are tested HIGH to LOW; if the Total falls below every
// band's lower bound the formula returns "" (rather than guessing
// the lowest band, which used to mask data-entry mistakes).
function buildBandFormula(totalRef, grades, field) {
  if (!grades || grades.length === 0) return '=""';
  const sorted = [...grades].sort((a, b) => b.hi - a.hi);
  const esc = (v) => String(v ?? '').replace(/"/g, '""');
  let inner = '""';
  for (let i = sorted.length - 1; i >= 0; i--) {
    const g = sorted[i];
    inner = `IF(AND(${totalRef}>=${g.lo},${totalRef}<=${g.hi}),"${esc(g[field])}",${inner})`;
  }
  return `=IF(ISNUMBER(${totalRef}),${inner},"")`;
}

function openPrintWindow(innerHTML, title) {
  const w = window.open('', '_blank', 'width=1100,height=820');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head>
    <meta charset="utf-8" /><title>${escapeHtml(title)}</title>
    <style>${PRINT_CSS}</style>
  </head><body>${innerHTML}</body></html>`);
  w.document.close(); w.focus();
  setTimeout(() => w.print(), 300);
}

// Download an XML Spreadsheet 2003 document. The `.xml` extension and
// `application/vnd.ms-excel` MIME tell the OS to hand the file to
// Excel — Excel recognises the mso-application processing instruction
// at the top of the body and treats it as a native workbook.
function downloadXmlExcel(xml, filename) {
  // BOM helps Excel pick UTF-8 on Windows.
  const blob = new Blob(['\uFEFF', xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ────────────────────────────────────────────────────────────────
// The dialog
// ────────────────────────────────────────────────────────────────

// `mode` controls the dialog chrome:
//   'modal'    → centered Dialog with maxWidth=xl (default, staff app)
//   'pulldown' → full-screen sheet that slides down from the top
//                (teacher portal alternative — feels like an in-page
//                drawer without us having to refactor the body)
//
// `autoExport` (boolean) → as soon as the sheet's data has loaded,
//   automatically trigger the Excel export and close the dialog.
//   Used by the teacher portal's per-card "Export Excel" icon so a
//   teacher gets the template in one click without a visible dialog
//   round-trip.
export default function RawScoreSheetDialog({ open, onClose, bucket, mode = 'modal', autoExport = false }) {
  // Previewing somebody else's sheet: scoped to them, and read-only.
  const { viewAsId, viewAsParams } = useViewAs();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  // entries[`${sub}|${ind}`] = { [colKey]: value } — what's been typed
  const [entries, setEntries] = useState({});
  // status[`${sub}|${ind}`]   = 'idle' | 'pending' | 'saved' | 'error'
  const [status, setStatus]   = useState({});

  // Active subject tab — only one subject's table is visible at a
  // time so teachers don't scroll past every subject before reaching
  // the one they're entering.
  const [activeTab, setActiveTab] = useState(0);

  // School info — fed into the Excel template's title block so the
  // exported sheet carries the school's identity before teachers
  // mail it around.
  const [school, setSchool] = useState({});
  // Track whether autoExport has already fired for this open cycle so
  // re-renders don't trigger the download more than once.
  const autoExportFiredRef = useRef(false);
  useEffect(() => {
    if (open) {
      API.get('/setup/info').then(r => setSchool(r.data?.info || {})).catch(() => {});
    }
  }, [open]);

  // Debounce timers per row id — never let the API see more than one
  // request per row in flight at a time.
  const timersRef = useRef({});

  // Refs that always point at the LATEST state. queueSave's setTimeout
  // closure used to capture `entries` directly, which meant a timer
  // armed at keystroke N would fire 600ms later still holding the
  // entries snapshot from before keystroke N's setState committed.
  // Concretely: typing "7" then "0" (to make "70") armed a timer whose
  // closure carried `{col:"7"}` — that "7" was the value that got
  // saved, and the user saw their "70" snap back to "7" on reopen.
  // Reading from a ref at the moment the timer fires sidesteps the
  // stale-closure trap.
  //
  // The columns ref is initialised with [] (rather than `columns`)
  // because the `columns` useMemo is declared further down — touching
  // it here would trip JS's temporal-dead-zone and crash the dialog
  // with "Cannot access 'columns' before initialization". The sync
  // useEffect below copies the latest value in on every render.
  const entriesRef = useRef(entries);
  const dataRef    = useRef(data);
  const columnsRef = useRef([]);
  // The bucket the card opened, including its sitting. `saveRow` reads
  // it through a ref for the same reason it reads everything else that
  // way: it is called from a debounce and must never see a stale value.
  const bucketRef  = useRef(bucket);
  useEffect(() => { entriesRef.current = entries; }, [entries]);
  useEffect(() => { dataRef.current    = data;    }, [data]);
  useEffect(() => { bucketRef.current  = bucket;  }, [bucket]);

  // ── load data + existing entries on open ────────────────────
  useEffect(() => {
    if (!open || !bucket) {
      setData(null); setEntries({}); setStatus({}); setError(''); setActiveTab(0);
      return;
    }
    setActiveTab(0);
    let cancelled = false;
    (async () => {
      setLoading(true); setError(''); setData(null);
      try {
        const params = {
          cla: bucket.cla, term: bucket.term,
          state: bucket.state, acc_yer: bucket.acc_yer || '',
          // Which sitting of a repeated exam (Mock 2, say). Without it
          // the subject list would carry every sitting's rows and the
          // sheet would show one tab per subject per mock.
          ...(bucket.exam_num ? { exam_num: bucket.exam_num } : {}),
          // An admin previewing a teacher's portal. The sheet must be
          // built for THAT teacher — otherwise it is built for the
          // admin, who is scoped to nothing, and a teacher assigned one
          // subject is shown a tab for every subject in the class.
          ...(viewAsParams || {}),
        };
        const [sheetRes, entryRes] = await Promise.all([
          API.get('/examination/score-sheet',         { params }),
          API.get('/examination/score-sheet/entries', { params }),
        ]);
        if (cancelled) return;
        setData(sheetRes.data);
        const map = {};
        for (const e of entryRes.data || []) {
          map[`${e.sub}|${e.ind}`] = e.items || {};
        }
        setEntries(map);
      } catch (e) {
        if (!cancelled) setError(swal.pickError(e, 'Could not load score sheet'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      // Flush every still-pending autosave so closing the dialog mid-
      // debounce doesn't drop the last edits the teacher made.
      flushRef.current();
    };
    // `viewAsParams` is memoised on the id by useViewAs, so listing it
    // here re-fetches when the previewed teacher changes without
    // re-fetching on every render.
  }, [open, bucket, viewAsParams]);

  const columns = useMemo(
    () => data ? buildColumns(data.profile, data.state) : [],
    [data]
  );
  // Keep the columnsRef in sync — initialised with [] above (TDZ
  // workaround), updated whenever columns changes.
  useEffect(() => { columnsRef.current = columns; }, [columns]);

  // Live total computed from current entries — also goes to autosave.
  const totalFor = useCallback((sub, ind) => {
    const items = entries[`${sub}|${ind}`] || {};
    return computeTotal(columns, items, data?.profile, data?.state);
  }, [entries, columns, data]);

  // Save one row immediately. Always reads the latest entries/columns/
  // profile via refs — never the closure from the call site — so the
  // value typed last is the value sent to the server.
  const saveRow = useCallback(async (sub, ind) => {
    const id    = `${sub}|${ind}`;
    const items = entriesRef.current[id] || {};
    const cols  = columnsRef.current;
    const d     = dataRef.current;
    if (!d) return;
    // A preview never writes. Examination Settings calls this a
    // "Readonly view of what each teacher sees", and it has to be one:
    // marks are keyed by (class, term, exam, year, subject, student)
    // with no record of who typed them, so an admin looking around
    // would silently overwrite the teacher's own work.
    if (viewAsId) { setStatus(s => ({ ...s, [id]: 'readonly' })); return; }
    try {
      const total = computeTotal(cols, items, d.profile, d.state);
      await API.post('/examination/score-sheet/entry', {
        cla: d.class, term: d.term, state: d.state,
        acc_yer: d.acc_yer || '',
        // WHICH SITTING. Without it every mock saved onto the same row,
        // so entering Mock 2 replaced Mock 1 with no warning. Read from
        // the bucket the card opened, not from `data` — the server
        // echoes the exam type back but not the sitting.
        ...(bucketRef.current?.exam_num ? { exam_num: bucketRef.current.exam_num } : {}),
        sub, ind, items, total,
      });
      setStatus(s => ({ ...s, [id]: 'saved' }));
    } catch (e) {
      setStatus(s => ({ ...s, [id]: 'error' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const queueSave = useCallback((sub, ind) => {
    const id = `${sub}|${ind}`;
    setStatus(s => ({ ...s, [id]: 'pending' }));
    clearTimeout(timersRef.current[id]);
    timersRef.current[id] = setTimeout(() => {
      // Drop the timer from the registry so flush() doesn't try to
      // fire a save we're already running.
      delete timersRef.current[id];
      saveRow(sub, ind);
    }, 600);
  }, [saveRow]);

  // Flush every pending debounce immediately — called on dialog close
  // so closing mid-debounce doesn't drop the last edits the user made.
  const flushPendingSaves = useCallback(() => {
    for (const [id, t] of Object.entries(timersRef.current)) {
      clearTimeout(t);
      delete timersRef.current[id];
      const [sub, ind] = id.split('|');
      saveRow(sub, ind);
    }
  }, [saveRow]);

  // The useEffect cleanup needs to call the *latest* flush (with the
  // latest saveRow closure), but the cleanup only sees the function
  // reference captured when the effect ran. Holding flush in a ref
  // lets the cleanup invoke whatever the most recent version is.
  const flushRef = useRef(flushPendingSaves);
  useEffect(() => { flushRef.current = flushPendingSaves; }, [flushPendingSaves]);

  // Gentle warning banner — shown when a typed value would exceed the
  // column's allowed max. The key forces React to remount the Snackbar
  // when the same message fires twice in a row (so a second over-max
  // attempt re-triggers the bump).
  const [notice, setNotice] = useState(null);   // { msg, key } | null
  const flashNotice = useCallback((msg) => {
    setNotice({ msg, key: Date.now() });
  }, []);

  // Validation gate. Accepts the proposed string, returns true when it
  // can be stored. Empty / partial input (a lone "-" while editing,
  // empty string when clearing) is always allowed — we only block
  // full numbers that exceed the cell's max.
  const acceptValue = useCallback((value, col) => {
    if (value === '' || value === '-' || value === '.') return true;
    const n = Number(value);
    if (!Number.isFinite(n)) return false;
    const max = Number(col?.max) || 100;
    if (n > max) {
      flashNotice(`"${value}" is over the ${max} max for ${col.header}. Stay between 0 and ${max}.`);
      return false;
    }
    if (n < 0) {
      flashNotice('Marks can\'t be negative — enter 0 or above.');
      return false;
    }
    return true;
  }, [flashNotice]);

  const onChangeCell = (sub, ind, col, value) => {
    if (!acceptValue(value, col)) return;
    const id = `${sub}|${ind}`;
    setEntries(prev => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [col.key]: value },
    }));
    queueSave(sub, ind);
  };

  // Arrow-key navigation across the cell grid — like a spreadsheet.
  // Up/Down move within the same column across students; Left/Right
  // move within the same row across mark columns. Each input carries
  // data-sheet / data-row / data-col attrs so we can find the next
  // input with a single querySelector. Movement is scoped to one
  // subject's table (data-sheet) — teachers almost always enter one
  // subject at a time and crossing sheets would be confusing.
  const onCellKeyDown = useCallback((e) => {
    const k = e.key;
    if (k !== 'ArrowUp' && k !== 'ArrowDown'
        && k !== 'ArrowLeft' && k !== 'ArrowRight'
        && k !== 'Enter') return;
    const el = e.currentTarget;
    const sheet = el.dataset.sheet;
    const row   = Number(el.dataset.row);
    const col   = Number(el.dataset.col);
    if (sheet == null || Number.isNaN(row) || Number.isNaN(col)) return;
    let nextRow = row, nextCol = col;
    if (k === 'ArrowUp')                   nextRow -= 1;
    else if (k === 'ArrowDown' || k === 'Enter') nextRow += 1;
    else if (k === 'ArrowLeft') {
      // Left at column 0 wraps to the previous row's last column —
      // matches Excel's default.
      if (col === 0) { nextRow -= 1; nextCol = 9999; }
      else nextCol -= 1;
    } else if (k === 'ArrowRight') {
      nextCol += 1;
    }
    // querySelector lookup. If the exact (row,col) doesn't exist, fall
    // back to the highest available col on the target row (handles the
    // ArrowLeft wrap case + jumping into a longer/shorter row).
    let target = document.querySelector(
      `input[data-sheet="${CSS.escape(sheet)}"][data-row="${nextRow}"][data-col="${nextCol}"]`
    );
    if (!target) {
      const candidates = document.querySelectorAll(
        `input[data-sheet="${CSS.escape(sheet)}"][data-row="${nextRow}"]`
      );
      target = candidates[candidates.length - 1];
    }
    if (target) {
      e.preventDefault();
      target.focus();
      target.select();
    }
  }, []);

  // Synthetic getRowValues for the print + Excel snapshots
  const getRowValues = (sub, ind) => entries[`${sub}|${ind}`] || {};

  const modeLabel = !data ? ''
    : data.profile?.mode === 'sba' ? 'SBA'
    : 'Simple';

  const handlePrint = () => {
    if (!data) return;
    const html = `<style>${PRINT_REPORT_CSS}</style>${htmlForPrint(data, columns, getRowValues, school)}`;
    openPrintWindow(html, `Score Sheet - ${data.class} - ${data.term} - ${data.state}`);
  };

  const handleExcel = useCallback(async () => {
    if (!data) return;
    try {
      // Flush any pending autosaves so the export captures whatever
      // the teacher just typed, then hand the live entries map to the
      // xlsx builder so every mark cell is pre-filled.
      flushRef.current();
      const buffer = await buildXlsxBuffer(data, columns, school, entries);
      const fname = `score-sheet-${data.class}-${data.term}-${data.state}`
        .replace(/[^A-Za-z0-9_-]+/g, '_') + '.xlsx';
      downloadXlsx(buffer, fname);
    } catch (e) {
      console.error(e);
      swal.error(e?.message || 'Excel export failed');
    }
  }, [data, columns, school, entries]);

  // autoExport — fire handleExcel once as soon as data + school are
  // loaded, then close. Reset the latch whenever the dialog closes so
  // a subsequent open from a different card fires again.
  useEffect(() => {
    if (!open) { autoExportFiredRef.current = false; return; }
    if (!autoExport) return;
    if (autoExportFiredRef.current) return;
    if (!data || !data.subjects || data.subjects.length === 0) return;
    autoExportFiredRef.current = true;
    handleExcel().finally(() => {
      // Give the browser a tick to start the download before closing.
      setTimeout(() => onClose?.(), 400);
    });
  }, [open, autoExport, data, school, handleExcel, onClose]);

  const isPulldown = mode === 'pulldown';

  return (
    <Dialog open={open} onClose={onClose}
      maxWidth={isPulldown ? false : 'xl'}
      fullWidth={!isPulldown}
      fullScreen={isPulldown}
      TransitionComponent={isPulldown ? SlideDown : undefined}
      PaperProps={{ sx: { borderRadius: isPulldown ? 0 : 2 } }}>
      <Box sx={{
        px: 3, py: 1.8,
        display: 'flex', alignItems: 'center', gap: 1.5,
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff',
      }}>
        <SheetIcon />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} sx={{ fontSize: 16, lineHeight: 1.15 }}>
            Raw Score Sheet
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.92 }}>
            {bucket
              ? `${bucket.cla} · ${bucket.term} · ${bucket.state}${bucket.acc_yer ? ` · ${bucket.acc_yer}` : ''}`
              : ''}
          </Typography>
        </Box>
        {modeLabel && (
          <Chip label={`${modeLabel} mode`} size="small"
            sx={{ fontWeight: 800, bgcolor: 'rgba(255,255,255,0.22)', color: '#fff' }} />
        )}
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ bgcolor: '#eef0f4', p: 2 }}>
        {loading && (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 1.5 }} color="text.secondary">Building sheet…</Typography>
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && data && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
              <Chip size="small" color="primary"
                label={`${data.subjects.length} subject${data.subjects.length === 1 ? '' : 's'}`} />
              <Chip size="small" color="info"
                label={`${data.students.length} student${data.students.length === 1 ? '' : 's'}`} />
              <Chip size="small" variant="outlined"
                label={`${columns.length} mark column${columns.length === 1 ? '' : 's'} per subject`} />
              {data.profile?.name && (
                <Chip size="small" variant="outlined" label={`Profile: ${data.profile.name}`} />
              )}
              <Box sx={{ flex: 1 }} />
              {/* Say which of the two this is. A sheet that autosaves
                  and a sheet that cannot save look identical until you
                  type into one. */}
              {viewAsId ? (
                <Chip size="small" variant="outlined" color="warning"
                  label="Read-only preview — typing here saves nothing" />
              ) : (
                <Chip size="small" variant="outlined" color="success"
                  icon={<SavedIcon sx={{ fontSize: 16 }} />}
                  label="Entries autosave per row (no Save button)" />
              )}
            </Stack>

            <Box sx={{ bgcolor: '#fff', borderRadius: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
              <style>{PRINT_CSS}</style>
              {data.subjects.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Alert severity="info" variant="outlined">
                    No subjects registered for this bucket. Open Register Subject to add some.
                  </Alert>
                </Box>
              )}

              {data.subjects.length > 0 && (
                <Tabs
                  value={Math.min(activeTab, data.subjects.length - 1)}
                  onChange={(_, v) => setActiveTab(v)}
                  variant="scrollable" scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    px: 1,
                    bgcolor: '#f5f7fb',
                    borderBottom: '2px solid #c5cae9',
                    minHeight: 42,
                    '& .MuiTab-root': {
                      minHeight: 42, py: 0.5, px: 2,
                      fontWeight: 700, textTransform: 'none',
                      fontSize: 13.5, color: '#37474f',
                      borderRadius: '8px 8px 0 0',
                      mr: 0.5,
                    },
                    '& .MuiTab-root.Mui-selected': {
                      color: '#1a237e',
                      bgcolor: '#fff',
                      boxShadow: 'inset 0 -2px 0 #1a237e',
                    },
                    '& .MuiTabs-indicator': { display: 'none' },
                  }}>
                  {data.subjects.map((s, i) => (
                    <Tab key={s.tr || s.sub}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <SheetIcon sx={{ fontSize: 16 }} />
                          {s.sub}
                        </Box>
                      } />
                  ))}
                </Tabs>
              )}

              <Box sx={{ p: 2 }}>
                {data.subjects.map((s, sIdx) => (
                  <Box key={s.tr || s.sub}
                    sx={{ display: sIdx === Math.min(activeTab, data.subjects.length - 1) ? 'block' : 'none' }}>
                    <SubjectSheet
                      subject={s}
                      sheetId={`s${sIdx}`}
                      data={data}
                      columns={columns}
                      entries={entries}
                      status={status}
                      onChangeCell={onChangeCell}
                      onCellKeyDown={onCellKeyDown}
                      totalFor={totalFor} />
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      {/* Gentle validation banner — only renders when a typed value
          would exceed the cell's max. Anchored to the top of the
          dialog so it can't shift the table rows around mid-entry. */}
      <Snackbar key={notice?.key}
        open={!!notice}
        autoHideDuration={3200}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setNotice(null)}
        sx={{ mt: 7 }}>
        <Alert severity="warning" variant="filled" onClose={() => setNotice(null)}
          sx={{ fontWeight: 700, boxShadow: '0 6px 18px rgba(0,0,0,0.25)' }}>
          {notice?.msg}
        </Alert>
      </Snackbar>

      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: 'background.paper',
        borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>Close</Button>
        <Tooltip title="Download Excel workbook — one tab per subject, mark cells unlocked, everything else (name, total, position, grade, remarks) is read-only">
          <span>
            <Button startIcon={<ExcelIcon />} onClick={handleExcel}
              disabled={!data || loading}
              variant="outlined" color="success"
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              Export Excel (tabbed · locked)
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Open the print-ready view with current values">
          <span>
            <Button startIcon={<PrintIcon />} onClick={handlePrint}
              disabled={!data || loading}
              variant="contained"
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              Print
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}

// One <table> per subject. Inputs onChange route through onChangeCell
// which optimistically updates entries[] and schedules a debounced
// autosave per row. Each input also carries data-sheet/row/col so the
// parent's keydown handler can jump focus on arrow keys.
function SubjectSheet({ subject, sheetId, data, columns, entries, status,
                       onChangeCell, onCellKeyDown, totalFor }) {
  // Snapshot every student's current total, then rank — done once per
  // render so Position/Grade/Remarks columns stay in sync with the
  // last keystroke.
  const positions = useMemo(() => {
    const rows = data.students.map(stu => ({
      ind: stu.ind,
      total: totalFor(subject.sub, stu.ind),
    }));
    return computePositions(rows);
  }, [data.students, subject.sub, totalFor]);

  const gradesList = data.grades && data.grades.length > 0 ? data.grades : DEFAULT_GRADES;

  return (
    <div className="rss-sheet">
      <div className="rss-title">{subject.sub}</div>
      <div className="rss-meta">
        Class: <b>{data.class}</b> &nbsp;·&nbsp;
        Term: <b>{data.term}</b> &nbsp;·&nbsp;
        Exam: <b>{data.state}</b>
        {data.acc_yer && <> &nbsp;·&nbsp; Year: <b>{data.acc_yer}</b></>}
        {subject.teacher && <><br/>Teacher: <b>{subject.teacher}</b></>}
      </div>
      <div className="rss-scroll">
        <table className="rss">
          <thead>
            <tr>
              <HeaderCell tip="Row number">#</HeaderCell>
              <HeaderCell tip="Student index number">Index</HeaderCell>
              <HeaderCell tip="Student full name" style={{ textAlign: 'left', paddingLeft: 8 }}>Student Name</HeaderCell>
              {columns.map(c => (
                <HeaderCell key={c.key} tip={c.header} className="head-mark">
                  {compactHeader(c.header)}
                </HeaderCell>
              ))}
              <HeaderCell tip="Total — auto-calculated from the marks above using the active assessment profile" className="head-total">Total</HeaderCell>
              <HeaderCell tip="Position in class — ranked by Total" className="head-pos">Pos</HeaderCell>
              <HeaderCell tip="Grade band from the school's grading scale" className="head-grade">Grade</HeaderCell>
              <HeaderCell tip="Remark for the grade band" className="head-remarks">Remarks</HeaderCell>
            </tr>
          </thead>
          <tbody>
            {data.students.length === 0 && (
              <tr><td colSpan={7 + columns.length} style={{ textAlign: 'center', color: '#666' }}>
                No students in this class.
              </td></tr>
            )}
            {data.students.map((stu, r) => {
              const id = `${subject.sub}|${stu.ind}`;
              const vals = entries[id] || {};
              const total = totalFor(subject.sub, stu.ind);
              const pos   = positions[stu.ind];
              const band  = gradeFor(total, gradesList);
              const st    = status[id];
              return (
                <tr key={stu.ind}>
                  <td className="cell-num">{r + 1}</td>
                  <td className="cell-ind">{stu.ind}</td>
                  <td className="cell-name">
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{stu.name}</span>
                      <RowStatus state={st} />
                    </Box>
                  </td>
                  {columns.map((c, ci) => (
                    <td className="cell-mark" key={c.key}>
                      <input type="number"
                        inputMode="decimal"
                        step="0.5"
                        min={0}
                        max={c.max || 100}
                        value={vals[c.key] ?? ''}
                        data-sheet={sheetId}
                        data-row={r}
                        data-col={ci}
                        onKeyDown={onCellKeyDown}
                        onChange={e => onChangeCell(subject.sub, stu.ind, c, e.target.value)} />
                    </td>
                  ))}
                  <td className="cell-total">{total === null ? '' : Number(total).toFixed(2)}</td>
                  <td className="cell-pos">{ordinal(pos)}</td>
                  <td className="cell-grade">{band?.grade || ''}</td>
                  <td className="cell-remarks">{band?.remarks || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="rss-formula">{formulaText(columns, data.profile, data.state)}</div>
    </div>
  );
}

// Sticky-friendly header cell with a quick-appearing MUI tooltip.
// The cursor: help affordance hints that more info is available.
function HeaderCell({ tip, children, className, style }) {
  return (
    <th className={className} style={style}>
      <Tooltip title={tip || ''} arrow placement="top" enterDelay={200} enterNextDelay={100}
        componentsProps={{
          tooltip: { sx: { fontSize: 12, fontWeight: 600, px: 1.25, py: 0.75, bgcolor: '#1a237e' } },
          arrow:   { sx: { color: '#1a237e' } },
        }}>
        <span style={{ display: 'inline-block', cursor: 'help', width: '100%' }}>
          {children}
        </span>
      </Tooltip>
    </th>
  );
}

function RowStatus({ state }) {
  if (state === 'pending')
    return <Tooltip title="Saving…"><PendingIcon sx={{ fontSize: 16, color: 'warning.main' }} /></Tooltip>;
  if (state === 'saved')
    return <Tooltip title="Saved"><SavedIcon sx={{ fontSize: 16, color: 'success.main' }} /></Tooltip>;
  if (state === 'error')
    return <Tooltip title="Save failed — keep typing to retry"><ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} /></Tooltip>;
  return null;
}
